import React from "react";

import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Tooltip from "@material-ui/core/Tooltip";
import Skeleton from "@material-ui/lab/Skeleton";

import { makeStyles } from "@material-ui/core/styles";

import { CheckCircle2 as CheckCircleIcon, AlertCircle as ErrorIcon } from "lucide-react";
import moment from "moment";

import Rating from "@material-ui/lab/Rating";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};
  const isDark = theme.palette.type === "dark" || theme.mode === "dark";
  return {
    mainPaper: {
      borderRadius: ds.radius?.md ?? 12,
      background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
      border: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
      boxShadow: "none",
      overflow: "hidden",
    },
    excelTable: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: 0,
      "& .MuiTableCell-root": {
        borderBottom: `1px solid ${dsColors.borderLight || (isDark ? "#1a1f29" : "#f0f0f0")}`,
        borderRight: "none",
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
        fontSize: 13.5,
        padding: "14px 16px",
      },
      "& .MuiTableCell-head": {
        background: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.03)",
        color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
        fontWeight: 700,
        fontSize: 11.5,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        borderBottom: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
        padding: "12px 16px",
        verticalAlign: "middle",
        whiteSpace: "nowrap",
      },
      "& .MuiTableCell-head:first-child": {
        borderTopLeftRadius: ds.radius?.md ?? 12,
      },
      "& .MuiTableCell-head:last-child": {
        borderTopRightRadius: ds.radius?.md ?? 12,
      },
      "& tbody tr:last-child .MuiTableCell-root": {
        borderBottom: "none",
      },
    },
    zebraRow: {
      transition: "background-color 0.18s ease",
      "&:hover": {
        backgroundColor: dsColors.surfaceHover || (isDark ? "#171b24" : "#f3f4f6"),
      },
    },
    statusOn: {
      color: "#2EA85A",
      display: "inline-flex",
    },
    statusOff: {
      color: isDark ? "#5b6770" : "#94a3b8",
      display: "inline-flex",
    },
    statusBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 10px",
      borderRadius: 12,
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.4,
    },
    statusBadgeOn: {
      background: "rgba(46,168,90,0.12)",
      color: "#2EA85A",
    },
    statusBadgeOff: {
      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
      color: isDark ? "#8696a0" : "#667781",
    },
    nameCell: {
      fontWeight: 600,
    },
  };
});

export function RatingBox({ rating }) {
  const ratingTrunc = rating === null ? 0 : Math.trunc(rating);
  return <Rating defaultValue={ratingTrunc} max={3} readOnly />;
}

function formatTime(minutes) {
  return moment().startOf("day").add(minutes, "minutes").format("HH[h] mm[m]");
}

export default function TableAttendantsStatus(props) {
  const { loading, attendants } = props;
  const classes = useStyles();

  return !loading ? (
    <TableContainer component={Paper} className={classes.mainPaper}>
      <Table className={classes.excelTable}>
        <TableHead>
          <TableRow>
            <TableCell>{i18n.t("dashboard.users.name")}</TableCell>
            <TableCell align="center">{i18n.t("dashboard.assessments.score")}</TableCell>
            <TableCell align="center">{i18n.t("dashboard.assessments.ratedCalls")}</TableCell>
            <TableCell align="center">{i18n.t("dashboard.assessments.totalCalls")}</TableCell>
            <TableCell align="center">{i18n.t("dashboard.cards.averageWaitingTime")}</TableCell>
            <TableCell align="center">{i18n.t("dashboard.cards.averageServiceTime")}</TableCell>
            <TableCell align="center">{i18n.t("dashboard.cards.status")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {attendants.map((a, k) => (
            <TableRow key={k} className={classes.zebraRow}>
              <TableCell className={classes.nameCell}>{a.name}</TableCell>
              <TableCell align="center">{a.rating}</TableCell>
              <TableCell align="center">{a.countRating}</TableCell>
              <TableCell align="center">{a.tickets}</TableCell>
              <TableCell align="center">{formatTime(a.avgWaitTime, 2)}</TableCell>
              <TableCell align="center">{formatTime(a.avgSupportTime, 2)}</TableCell>
              <TableCell align="center">
                {a.online ? (
                  <Tooltip title={i18n.t("dashboard.status.online")}>
                    <span className={`${classes.statusBadge} ${classes.statusBadgeOn}`}>
                      <CheckCircleIcon size={12} />
                      {i18n.t("dashboard.status.online")}
                    </span>
                  </Tooltip>
                ) : (
                  <Tooltip title={i18n.t("dashboard.status.offline")}>
                    <span className={`${classes.statusBadge} ${classes.statusBadgeOff}`}>
                      <ErrorIcon size={12} />
                      {i18n.t("dashboard.status.offline")}
                    </span>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  ) : (
    <Skeleton variant="rect" height={150} />
  );
}
